import type { Plugin } from "unified";
import type { Element, ElementContent, Root, Text } from "hast";
import { SKIP, visitParents } from "unist-util-visit-parents";
import { QCFinding } from "../../types/qcTypes";

// Rehype plugin that, given a list of QC findings whose offsets refer to the
// markdown source, walks the hast and:
//   1. Wraps the text spanning each finding's [StartOffset, EndOffset) range in
//      a <span data-qc-finding-id="..." data-qc-kind="original"> element.
//   2. Inserts sibling spans after the last original-wrap: one for the
//      suggested text (if any) and an empty placeholder where the action
//      buttons get rendered.
//
// hast text nodes inherit `position.start.offset` from mdast (via
// mdast-util-to-hast), so source offsets map precisely onto hast text-node
// ranges. A finding straddling inline formatting becomes multiple adjacent
// wraps -- the .findingOriginal CSS makes them flow as one strikethrough.
//
// When a finding's SuggestedText is a whole markdown table row
// ("| a | b | c |") and its original sits inside a table cell, the raw row
// string would show literal pipes. In that case the suggestion is split per
// cell and each cell's replacement is placed inside the matching cell.

export const QC_ATTR_FINDING_ID = "dataQcFindingId";
export const QC_ATTR_KIND = "dataQcKind";

export const QC_KIND_ORIGINAL = "original";
export const QC_KIND_SUGGESTED = "suggested";
export const QC_KIND_ACTIONS = "actions";

const hasOffsets = (
  node: { position?: Text["position"] },
): node is { position: NonNullable<Text["position"]> } =>
  node.position != null &&
  typeof node.position.start?.offset === "number" &&
  typeof node.position.end?.offset === "number";

export const rehypeQcFindings: Plugin<[QCFinding[]], Root> = (findings) => {
  return (tree: Root) => {
    if (!findings || findings.length === 0) return;
    const sorted = [...findings].sort((a, b) => a.StartOffset - b.StartOffset);

    for (const finding of sorted) {
      wrapFindingRange(tree, finding);
    }
    for (const finding of sorted) {
      appendSuggestedAndActions(tree, finding);
    }
  };
};

function wrapFindingRange(tree: Root, finding: QCFinding) {
  if (finding.EndOffset <= finding.StartOffset) return;
  const findingId = String(finding.FindingID);

  visitParents(tree, "text", (node, ancestors) => {
    if (!hasOffsets(node)) return;
    const ns = node.position.start.offset as number;
    const ne = node.position.end.offset as number;
    if (finding.StartOffset >= ne || finding.EndOffset <= ns) return;

    const parent = ancestors[ancestors.length - 1] as
      | Element
      | Root
      | undefined;
    if (!parent || !("children" in parent)) return;
    const children = parent.children as ElementContent[];
    const idx = children.indexOf(node as unknown as ElementContent);
    if (idx < 0) return;

    const startInNode = Math.max(0, finding.StartOffset - ns);
    const endInNode = Math.min(node.value.length, finding.EndOffset - ns);
    if (startInNode >= endInNode) return;

    const before = node.value.slice(0, startInNode);
    const middle = node.value.slice(startInNode, endInNode);
    const after = node.value.slice(endInNode);
    if (!middle) return;

    const replacement: ElementContent[] = [];
    if (before) {
      replacement.push({
        type: "text",
        value: before,
        position: {
          start: node.position.start,
          end: {
            offset: ns + before.length,
            line: node.position.start.line,
            column: node.position.start.column,
          },
        },
      });
    }
    replacement.push({
      type: "element",
      tagName: "span",
      properties: {
        [QC_ATTR_FINDING_ID]: findingId,
        [QC_ATTR_KIND]: QC_KIND_ORIGINAL,
      },
      children: [{ type: "text", value: middle }],
      position: {
        start: {
          offset: ns + startInNode,
          line: node.position.start.line,
          column: node.position.start.column,
        },
        end: {
          offset: ns + endInNode,
          line: node.position.end.line,
          column: node.position.end.column,
        },
      },
    });
    if (after) {
      replacement.push({
        type: "text",
        value: after,
        position: {
          start: {
            offset: ns + endInNode,
            line: node.position.start.line,
            column: node.position.start.column,
          },
          end: node.position.end,
        },
      });
    }

    children.splice(idx, 1, ...replacement);
    return [SKIP, idx + replacement.length];
  });
}

const TABLE_CELL_TAGS = new Set(["td", "th"]);

// Parse a single markdown table-row string ("| a | b | c |") into trimmed
// cell values. Returns null when the text is not a one-line pipe-delimited
// row, so ordinary suggestions are left untouched.
function parseTableRow(text: string): string[] | null {
  const trimmed = text.trim();
  if (trimmed.includes("\n")) return null;
  if (!trimmed.startsWith("|") || !trimmed.endsWith("|")) return null;
  if ((trimmed.match(/\|/g) || []).length < 3) return null;
  return trimmed
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function makeQcSpan(
  findingId: string,
  kind: string,
  text?: string,
): Element {
  return {
    type: "element",
    tagName: "span",
    properties: {
      [QC_ATTR_FINDING_ID]: findingId,
      [QC_ATTR_KIND]: kind,
    },
    children: text != null ? [{ type: "text", value: text }] : [],
  };
}

interface OriginalWrap {
  parent: Element | Root;
  indexInParent: number;
  cell: Element | null;
  columnIndex: number;
}

// Find every original-wrap span for a finding, recording the table cell that
// contains it (if any) and that cell's column index within its row.
function collectOriginalWraps(tree: Root, findingId: string): OriginalWrap[] {
  const wraps: OriginalWrap[] = [];

  visitParents(tree, "element", (node, ancestors) => {
    const el = node as Element;
    if (el.tagName !== "span") return;
    const props = el.properties as Record<string, unknown> | undefined;
    if (!props) return;
    if (props[QC_ATTR_FINDING_ID] !== findingId) return;
    if (props[QC_ATTR_KIND] !== QC_KIND_ORIGINAL) return;

    const parent = ancestors[ancestors.length - 1] as
      | Element
      | Root
      | undefined;
    if (!parent || !("children" in parent)) return;
    const indexInParent = (parent.children as ElementContent[]).indexOf(
      el as unknown as ElementContent,
    );
    if (indexInParent < 0) return;

    let cell: Element | null = null;
    let cellAncestorIdx = -1;
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const anc = ancestors[i] as Element;
      if (anc.tagName && TABLE_CELL_TAGS.has(anc.tagName)) {
        cell = anc;
        cellAncestorIdx = i;
        break;
      }
    }

    let columnIndex = -1;
    if (cell && cellAncestorIdx > 0) {
      const row = ancestors[cellAncestorIdx - 1] as Element;
      if (row && Array.isArray(row.children)) {
        let col = 0;
        for (const child of row.children) {
          if (child === cell) {
            columnIndex = col;
            break;
          }
          if (
            child.type === "element" &&
            TABLE_CELL_TAGS.has((child as Element).tagName)
          ) {
            col++;
          }
        }
      }
    }

    wraps.push({ parent, indexInParent, cell, columnIndex });
  });

  return wraps;
}

function appendSuggestedAndActions(tree: Root, finding: QCFinding) {
  const findingId = String(finding.FindingID);
  const wraps = collectOriginalWraps(tree, findingId);
  if (wraps.length === 0) return;

  // User-edited replacement takes precedence over the AI suggestion so the
  // redline preview matches what apply will write. When the finding had no
  // SuggestedText but the user supplied an EditedText, this also makes a
  // suggested span appear where one would not have existed before.
  const replacementText =
    finding.EditedText != null ? finding.EditedText : finding.SuggestedText;

  const suggestedCells = replacementText
    ? parseTableRow(replacementText)
    : null;

  // The suggestion is a whole table row and every original wrap sits in a
  // table cell -> drop one suggested span per cell so no raw pipes show. A
  // suggestion with FEWER cells than the row has columns (the model dropped a
  // trailing cell) still maps -- columns past the suggestion get an empty
  // replacement -- so a short row never falls back to rendering raw `| ... |`.
  const useCellMapping =
    suggestedCells !== null &&
    wraps.every((w) => w.cell !== null && w.columnIndex >= 0);

  if (useCellMapping) {
    const lastWrapByCell = new Map<Element, OriginalWrap>();
    for (const w of wraps) lastWrapByCell.set(w.cell as Element, w);
    const groups = [...lastWrapByCell.values()];

    // Each group is a distinct cell -> a distinct parent, so the indices
    // captured during collection stay valid across these splices.
    const cells = suggestedCells as string[];
    groups.forEach((w, groupIdx) => {
      // Columns past the suggestion's last cell get an empty replacement
      // rather than disabling cell mapping (which would show raw pipes).
      const cellText = w.columnIndex < cells.length ? cells[w.columnIndex] : "";
      const toInsert: ElementContent[] = [
        makeQcSpan(findingId, QC_KIND_SUGGESTED, cellText),
      ];
      if (groupIdx === groups.length - 1) {
        toInsert.push(makeQcSpan(findingId, QC_KIND_ACTIONS));
      }
      (w.parent.children as ElementContent[]).splice(
        w.indexInParent + 1,
        0,
        ...toInsert,
      );
    });
    return;
  }

  // Default: a single suggested span + actions after the last wrap.
  const last = wraps[wraps.length - 1];
  const toInsert: ElementContent[] = [];
  if (replacementText) {
    toInsert.push(makeQcSpan(findingId, QC_KIND_SUGGESTED, replacementText));
  }
  toInsert.push(makeQcSpan(findingId, QC_KIND_ACTIONS));
  (last.parent.children as ElementContent[]).splice(
    last.indexInParent + 1,
    0,
    ...toInsert,
  );
}
